import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CustomLayout from '../../components/CustomLayout';
import CustomSpinner from '../../components/CustomSpinner';
import AdminLogin from '../../components/AdminLogin';
import { fetchFunds } from '../../api/funds';
import { fetchScheduleData } from '../../api/admin';
import { useAdmin } from '../../contexts/AdminContext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { FilterMatchMode } from 'primereact/api';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './index.scss';

const AdminPanel = () => {
  const { t } = useTranslation();
  const { isAdminAuthenticated, login, logout, loading: authLoading } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [scheduleData, setScheduleData] = useState([]);
  const [error, setError] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedFunds, setSelectedFunds] = useState('');
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [funds, setFunds] = useState([]);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    firstName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    lastName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });
  const dt = useRef(null);

  const handleLogin = async (credentials) => {
    setLoginError(null);
    const result = await login(credentials);
    if (!result.success) {
      setLoginError(result.error);
    }
  };

  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    const loadFunds = async () => {
      try {
        const result = await fetchFunds();
        setFunds(result);
      } catch (error) {
        console.error('Error loading funds:', error);
      }
    };

    loadFunds();
  }, []);

  // Fetch data from API on component mount
  useEffect(() => {
    if (!isAdminAuthenticated) {
      setLoading(false);
      return;
    }

    const loadScheduleData = async () => {
      try {
        setLoading(true);
        // Fetch all data without backend filtering to ensure we get all transactions
        // Then apply client-side filtering based on actual transaction dates
        const result = await fetchScheduleData(null, null);
        const data = Array.isArray(result) ? result : [result];
        const transformedData = data.reduce((allTransations, donation) => {
          if (donation.donationScheduleBreakdown) {
            donation.donationScheduleBreakdown.forEach(schedule => {
              const status = schedule.status || donation.status || 'pending';

              if(status === 'cancelled') return;
              
              allTransations.push({
                ...schedule,
                firstName: donation.firstName,
                lastName: donation.lastName,
                customerId: donation.customerId,
                template: donation.template,
                scheduleId: donation.id, // Use the document ID from Firebase
                amount: schedule.allocations[0] ? schedule.allocations[0].amount - donation.autoAjrRecurringSupport : null,
                tipAmount: donation.autoAjrRecurringSupport,
                fundId: schedule.allocations[0] ? schedule.allocations[0].fundId : '1',
                status
              });
            });
          }
          return allTransations;
        }, []);
      
        
        setScheduleData(transformedData);
        setError(null);
      } catch (err) {
        setError('Failed to load schedule data');
        console.error('Error loading schedule data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadScheduleData();
  }, [isAdminAuthenticated]); // Only reload when authentication changes

  // Initialize filters from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const monthParam = urlParams.get('month');
    const yearParam = urlParams.get('year');
    const statusParam = urlParams.get('status');
    
    if (monthParam && monthParam >= '1' && monthParam <= '12') {
      setSelectedMonth(monthParam);
    }
    
    if (yearParam && !isNaN(parseInt(yearParam))) {
      setSelectedYear(yearParam);
    }
    
    if (statusParam) {
      setSelectedStatus(statusParam);
    }
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const urlParams = new URLSearchParams();
    
    if (selectedMonth) {
      urlParams.set('month', selectedMonth);
    }
    
    if (selectedYear && selectedYear !== new Date().getFullYear().toString()) {
      urlParams.set('year', selectedYear);
    }
    
    if (selectedStatus) {
      urlParams.set('status', selectedStatus);
    }
    
    const newSearch = urlParams.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
    
    // Update URL without causing a page reload
    window.history.replaceState({}, '', newUrl);
  }, [selectedMonth, selectedYear, selectedStatus]);

  const handleFilterChange = (month, year, status) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    if (status !== undefined) {
      setSelectedStatus(status);
    }
  };

  const clearFilters = () => {
    setSelectedMonth('');
    setSelectedYear(new Date().getFullYear().toString());
    setSelectedStatus('');
    setGlobalFilterValue('');
    setSelectedFunds('');
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      firstName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      lastName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      status: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear+1; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const monthOptions = [
    { value: '', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'paused', label: 'Paused' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' }
  ];

  const formatDate = (dateString) => {
    const date = dateString?.$date ? new Date(dateString.$date) : new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'neutral';
    }
  };

  // Helper function to determine if we should show completed or projected data
  const isShowingProjectedData = () => {
    const month = selectedMonth;
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const filterYear = selectedYear ? parseInt(selectedYear, 10) : currentYear;
    
    if (!month) {
      return false; // If no month selected, show completed
    }
    
    const filterMonthNum = parseInt(month, 10);
    
    // If selected year is in the past, show completed
    if (filterYear < currentYear) {
      return false;
    }
    // If selected year is current year
    else if (filterYear === currentYear) {
      // If month is in the future, show projected
      return filterMonthNum > currentMonth;
    }
    // If selected year is in the future, show projected
    else {
      return true;
    }
  };

  // Helper function to get display text for stat cards
  const getStatDisplayText = (type) => {
    const isProjected = isShowingProjectedData();
    const prefix = isProjected ? 'Projected' : 'Completed';
    const monthLabel = selectedMonth ? monthOptions.find(m => m.value === selectedMonth)?.label : (type === 'tips' ? 'all months' : 'All months');
    
    if (type === 'donations') {
      return `${prefix} Total Donation Amount for ${monthLabel} ${selectedYear}`;
    } else if (type === 'tips') {
      return `${prefix} Tips for AutoAjr for ${monthLabel} ${selectedYear}`;
    } else if (type === 'scheduled') {
      if (isProjected) {
        return `Scheduled Donations for ${monthLabel} ${selectedYear}`;
      } else {
        return `Completed Donations for ${monthLabel} ${selectedYear}`;
      }
    }
  };

  const getTotalDonationAmount = () => {
    const month = selectedMonth;
    const filterYear = selectedYear ? parseInt(selectedYear, 10) : new Date().getFullYear();
    const showCompleted = !isShowingProjectedData();

    // If scheduleData is array of transactions
    if (scheduleData.length > 0 && scheduleData[0].allocations[0] !== undefined) {
      return scheduleData.reduce((total, transaction) => {
        const transactionDate = transaction.date?.$date
          ? new Date(transaction.date.$date)
          : new Date(transaction.date);
        const transactionMonth = transactionDate.getMonth() + 1;
        const transactionYear = transactionDate.getFullYear();
        
        // Filter by month if selected
        const isInMonth = month ? transactionMonth === parseInt(month, 10) : true;
        
        // Filter by year if selected
        const isInYear = selectedYear ? transactionYear === filterYear : true;
        
        if (!isInMonth || !isInYear) return total;
        
        // If showing completed, only count completed transactions
        // If showing projected, count all non-cancelled transactions
        if (showCompleted) {
          return transaction.status === "completed" ? total + (transaction.allocations[0].amount - (transaction.tipAmount || 0)) : total;
        } else {
          // Projected: include all except cancelled
          return transaction.status !== "cancelled" ? total + (transaction.allocations[0].amount - (transaction.tipAmount || 0)) : total;
        }
      }, 0);
    }
    
    return 0;
  };

  const getTotalSchedules = () => {
    const month = selectedMonth;
    const filterYear = selectedYear ? parseInt(selectedYear, 10) : new Date().getFullYear();
    const showCompleted = !isShowingProjectedData();

    return scheduleData.filter((transaction) => {
      const transactionDate = transaction.date?.$date
        ? new Date(transaction.date.$date)
        : new Date(transaction.date);
      const transactionMonth = transactionDate.getMonth() + 1;
      const transactionYear = transactionDate.getFullYear();
      
      // Filter by month if selected
      const isInMonth = month ? transactionMonth === parseInt(month, 10) : true;
      
      // Filter by year if selected
      const isInYear = selectedYear ? transactionYear === filterYear : true;
      
      if (!isInMonth || !isInYear) return false;

      const status = transaction.status?.toLowerCase();
      
      // If showing completed (past/current month), count completed transactions
      // If showing projected (future month), count pending/scheduled transactions
      if (showCompleted) {
        return status === 'completed';
      } else {
        // For future months, count pending/scheduled (not completed or cancelled)
        return !status || (status !== 'completed' && status !== 'cancelled');
      }
    }).length;
  };

  const getTotalNumberOfTips = () => {
    const month = selectedMonth;
    const filterYear = selectedYear ? parseInt(selectedYear, 10) : new Date().getFullYear();
    const showCompleted = !isShowingProjectedData();

    if (scheduleData.length > 0 && scheduleData[0].allocations[0] !== undefined) {
      return scheduleData.reduce((total, transaction) => {
        const transactionDate = transaction.date?.$date
          ? new Date(transaction.date.$date)
          : new Date(transaction.date);
        const transactionMonth = transactionDate.getMonth() + 1;
        const transactionYear = transactionDate.getFullYear();
        
        // Filter by month if selected
        const isInMonth = month ? transactionMonth === parseInt(month, 10) : true;
        
        // Filter by year if selected
        const isInYear = selectedYear ? transactionYear === filterYear : true;
        
        if (!isInMonth || !isInYear) return total;
        
        // If showing completed, only count completed transactions
        // If showing projected, count all non-cancelled transactions
        if (showCompleted) {
          return transaction.status === "completed" ? total + (transaction.tipAmount || 0) : total;
        } else {
          // Projected: include all except cancelled
          return transaction.status !== "cancelled" ? total + (transaction.tipAmount || 0) : total;
        }
      }, 0);
    }
    
    return 0;
  }

  const getRecurrenceType = (template) => {
    switch (template) {
      case 'Fridays':
        return 'Weekly (Fridays)';
      case 'Hijri10':
        return 'Hijri 10 Days';
      case 'Lunar':
        return 'Lunar Monthly';
      case 'OneTime':
        return 'One Time';
      default:
        return template || 'Unknown';
    }
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <div className="datatable-header">
        <h2 className="section-title">
          Donation Transactions ({filteredData.length}{filteredData.length !== scheduleData.length ? ` of ${scheduleData.length}` : ''})
          {(selectedMonth || selectedYear !== new Date().getFullYear().toString() || selectedStatus) && (
            <span className="filter-indicator">
              - {selectedMonth ? monthOptions.find(m => m.value === selectedMonth)?.label : 'All months'} {selectedYear}
              {selectedStatus && ` • ${statusOptions.find(s => s.value === selectedStatus)?.label}`}
            </span>
          )}
        </h2>
        <div className="header-controls">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText 
              value={globalFilterValue} 
              onChange={onGlobalFilterChange} 
              placeholder="Search transactions..." 
            />
          </span>
          {scheduleData.length > 0 && scheduleData[0].customerId !== undefined && (
            <Button 
              label="Download CSV" 
              icon="pi pi-download" 
              onClick={exportCSV}
              className="p-button-success ml-auto"
            />
          )}
        </div>
      </div>
    );
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const statusBodyTemplate = (rowData) => {
    const status = rowData.status?.toLowerCase() || 'pending';
    const severity = status === 'completed' ? 'success' : status === 'failed' ? 'danger' : 'warning';
    return <Tag value={status.toUpperCase()} severity={severity} />;
  };

  const amountBodyTemplate = (rowData) => {
    return rowData.amount ? `$${rowData.amount}` : 'N/A';
  };

  const tipAmountBodyTemplate = (rowData) => {
    return `$${rowData.tipAmount || 0}`;
  };

  const fundBodyTemplate = (rowData) => {
    const fund = funds.find(f => f.id === rowData.fundId);
    return `${fund ? fund.name : 'Unknown Fund'}`;
  };

  const dateBodyTemplate = (rowData) => {
    return formatDate(rowData.date);
  };

  const recurrenceBodyTemplate = (rowData) => {
    const template = rowData.template?.toLowerCase() || '';
    return (
      <span className={`recurrence-badge ${template}`}>
        {getRecurrenceType(rowData.template)}
      </span>
    );
  };

  const donorNameBodyTemplate = (rowData) => {
    return `${rowData.firstName} ${rowData.lastName}`;
  };

  const manageDonationBodyTemplate = (rowData) => {
    const handleManageClick = () => {
      window.open(`/manage-donation/${rowData.scheduleId}`, '_blank');
    };
    
    return (
      <Button 
        icon="pi pi-cog" 
        label="Manage"
        onClick={handleManageClick}
        className="p-button-sm p-button-info"
        tooltip="Manage this donation"
        tooltipOptions={{ position: 'top' }}
      />
    );
  };

  const getUniqueDonors = () => {
    // If scheduleData is array of transactions
    if (scheduleData.length > 0 && scheduleData[0].customerId !== undefined) {
      const uniqueCustomers = new Set();
      scheduleData.forEach(transaction => {
        uniqueCustomers.add(transaction.customerId);
      });
      return uniqueCustomers.size;
    }
    // If scheduleData is array of donation records
    return scheduleData.length;
  };

  // Filter data based on month, year, and status
  const filteredData = scheduleData.filter(transaction => {
    if(selectedFunds) {
      if(transaction.allocations[0]?.fundId !== selectedFunds) {
        return false;
      }
    }
    // Filter by status if selected
    if (selectedStatus) {
      const transactionStatus = transaction.status?.toLowerCase() || 'pending';
      // If filtering for "pending", include transactions with no status or status === 'pending'
      if (selectedStatus === 'pending') {
        if (transaction.status && transaction.status.toLowerCase() !== 'pending') {
          return false;
        }
      } else {
        // For other statuses, must match exactly
        if (transactionStatus !== selectedStatus.toLowerCase()) {
          return false;
        }
      }
    }
    
    // Filter by month/year if selected
    if (selectedMonth || selectedYear) {
      const transactionDate = transaction.date?.$date 
        ? new Date(transaction.date.$date) 
        : new Date(transaction.date);
      
      const transactionMonth = transactionDate.getMonth() + 1; // getMonth() is 0-indexed
      const transactionYear = transactionDate.getFullYear();
      
      // Always check year if selectedYear is set
      if (selectedYear && transactionYear !== parseInt(selectedYear)) {
        return false;
      }
      
      // If month is selected, check month as well
      if (selectedMonth && transactionMonth !== parseInt(selectedMonth)) {
        return false;
      }
    }
    
    return true;
  });
  
  const paginatorLeft = <Button type="button" icon="pi pi-refresh" text onClick={clearFilters} />;
  const paginatorRight = <Button type="button" icon="pi pi-download" text onClick={exportCSV} />;

  // Show login form if not authenticated
  if (!isAdminAuthenticated) {
    return (
      <AdminLogin 
        onLogin={handleLogin}
        loading={authLoading}
        error={loginError}
      />
    );
  }

  if (loading) {
    const filterText = selectedMonth ? 
      `${monthOptions.find(m => m.value === selectedMonth)?.label} ${selectedYear}` :
      selectedYear !== new Date().getFullYear().toString() ? selectedYear : '';
    
    return (
      <CustomLayout
        title="Admin Dashboard"
        description={`Loading donation data${filterText ? ` for ${filterText}` : ''}...`}
      >
        <div className="admin-panel">
          <CustomSpinner />
        </div>
      </CustomLayout>
    );
  }

  if (error) {
    return (
      <CustomLayout
        title="Admin Dashboard"
        description="Error loading data"
      >
        <div className="admin-panel">
          <div className="error-message">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      </CustomLayout>
    );
  }

  if (!scheduleData || scheduleData.length === 0) {
    const hasActiveFilters = selectedMonth || selectedYear !== new Date().getFullYear().toString() || selectedStatus;
    const filterText = selectedMonth ? 
      `${monthOptions.find(m => m.value === selectedMonth)?.label} ${selectedYear}` :
      selectedYear !== new Date().getFullYear().toString() ? selectedYear : '';
    const statusText = selectedStatus ? statusOptions.find(s => s.value === selectedStatus)?.label : '';
    
    return (
        <div className="admin-panel">
          {/* Admin Header */}
          <div className="admin-header">
            <h1>Admin Dashboard</h1>
            <button className="logout-button" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>

          <div className="no-data-message">
            <div className="no-data-icon">📊</div>
            <h3>No Data Available</h3>
            {hasActiveFilters ? (
              <div className="no-data-with-filters">
                <p>No scheduled donations found for <strong>{filterText}{statusText && ` • Status: ${statusText}`}</strong>.</p>
                <p>Try adjusting your filter settings or clear all filters to see all data.</p>
                <button 
                  className="clear-filters-btn-large"
                  onClick={clearFilters}
                >
                  🗑️ Clear All Filters
                </button>
              </div>
            ) : (
              <p>There are no scheduled donations to display.</p>
            )}
          </div>
        </div>
    );
  }

  return (
      <div className="admin-panel">
        {/* Admin Header */}
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <button className="logout-button" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>

        {/* Monthly Filter */}
        <div className="filter-section">
          <div className="filter-container">
            <h3>Filter Transactions</h3>
            <div className="filter-controls">
              <div className="filter-group">
                <label htmlFor="month-select">Month:</label>
                <select
                  id="month-select"
                  value={selectedMonth}
                  onChange={(e) => handleFilterChange(e.target.value, selectedYear, selectedStatus)}
                  className="filter-select"
                >
                  {monthOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="year-select">Year:</label>
                <select
                  id="year-select"
                  value={selectedYear}
                  onChange={(e) => handleFilterChange(selectedMonth, e.target.value, selectedStatus)}
                  className="filter-select"
                >
                  {generateYearOptions().map(year => (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="status-select">Status:</label>
                <select
                  id="status-select"
                  value={selectedStatus}
                  onChange={(e) => handleFilterChange(selectedMonth, selectedYear, e.target.value)}
                  className="filter-select"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="funds-select">Funds:</label>
                <select
                  id="funds-select"
                  value={selectedFunds}
                  onChange={(e) => setSelectedFunds(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Funds</option>
                  {funds.map(option => (
                    <option key={option.fundId} value={option.fundId}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="clear-filters-btn"
                onClick={clearFilters}
                title="Clear all filters"
              >
                🗑️ Clear Filters
              </button>
            </div>
            {(selectedMonth || selectedYear !== new Date().getFullYear().toString() || selectedStatus) && (
              <div className="active-filters">
                <span className="filter-info">
                  Showing data for: {selectedMonth ? monthOptions.find(m => m.value === selectedMonth)?.label : 'All months'} {selectedYear}
                  {selectedStatus && ` • Status: ${statusOptions.find(s => s.value === selectedStatus)?.label}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Overview Stats */}
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>${getTotalDonationAmount()}</h3>
                <p>{getStatDisplayText('donations')}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>{getTotalSchedules()}</h3>
                <p>{getStatDisplayText('scheduled')}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>${getTotalNumberOfTips()}</h3>
                <p>{getStatDisplayText('tips')}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{getUniqueDonors()}</h3>
                <p>Total Donors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Table */}
        <div className="section-container">
          {/* Check if data is transaction format */}
          {scheduleData.length > 0 && scheduleData[0].customerId !== undefined ? (
            <>
              {filteredData.length === 0 ? (
                <div className="no-results-message">
                  <div className="no-results-content">
                    <div className="no-results-icon">🔍</div>
                    <h4>No transactions found</h4>
                    {(selectedMonth || selectedYear !== new Date().getFullYear().toString() || selectedStatus) ? (
                      <div>
                        <p>No transactions match your current filter criteria.</p>
                        <button 
                          className="clear-filters-btn-small"
                          onClick={clearFilters}
                        >
                          🗑️ Clear Filters
                        </button>
                      </div>
                    ) : (
                      <p>No transaction data available.</p>
                    )}
                  </div>
                </div>
              ) : (
                <DataTable 
                  ref={dt}
                  value={filteredData} 
                  paginator 
                  rows={25}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  header={renderHeader()}
                  filters={filters}
                  globalFilterFields={['firstName', 'lastName', 'customerId', 'scheduleId', 'status']}
                  emptyMessage="No transactions found."
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} transactions"
                  paginatorLeft={paginatorLeft}
                  paginatorRight={paginatorRight}
                  sortMode="multiple"
                  removableSort
                  stripedRows
                  showGridlines
                  size="small"
                  className="admin-datatable"
                >
                  <Column 
                    field="firstName" 
                    header="Name" 
                    body={donorNameBodyTemplate}
                    sortable 
                  />
                  <Column 
                    field="amount" 
                    header="Amount" 
                    body={amountBodyTemplate}
                    sortable 
                  />
                  <Column 
                    field="tipAmount" 
                    header="Tip" 
                    body={tipAmountBodyTemplate}
                    sortable 
                  />
                  <Column 
                    field="date" 
                    header="Date" 
                    body={dateBodyTemplate}
                    sortable 
                  />
                  <Column 
                    field="template" 
                    header="Recurrence Type" 
                    body={recurrenceBodyTemplate}
                    sortable 
                  />
                  <Column 
                    field="fund" 
                    header="Fund" 
                    body={fundBodyTemplate}
                    sortable 
                  />
                  <Column 
                    field="status" 
                    header="Status" 
                    body={statusBodyTemplate}
                    sortable 
                  />
                  <Column 
                    field="scheduleId" 
                    header="Manage Donation" 
                    body={manageDonationBodyTemplate}
                    sortable 
                  />
                </DataTable>
              )}
            </>
          ) : (
            /* Fallback message if data structure is different */
            <div className="fallback-message">
              <p>Transaction data format not recognized. Please check the API response format.</p>
            </div>
          )}
        </div>
      </div>
  );
};

export default AdminPanel;